import type { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Checkbox } from '@xing-com/checkbox';
import { useLocation } from '@xing-com/crate-core-router';
import {
  PasswordInput,
  FormMessage,
  ContinueWithApple,
  ContinueWithGoogle,
  Divider,
  FormHeadline,
  PasswordStrengthMeter,
} from '@xing-com/crate-login-layout';
import { useLoginTracking } from '@xing-com/crate-login-tracking';
import { getMainHost, getExtraParams } from '@xing-com/crate-login-utils';
import { useHost, useLoginAppUrl } from '@xing-com/crate-xinglet';
import { FormField } from '@xing-com/text-field';

import { FriendlyCaptcha, RegisterViaCvSection } from './components';
import { FRIENDLYCAPTCHA_KEY, FRIENDLYCAPTCHA_KEY_XB } from './constants';
import messages from './messages';
import {
  FormContainer,
  FakeInput,
  InputActions,
  InputWrapper,
  SubmitButton,
  Label,
  HiddenWrapper,
  CheckboxWrapper,
  TandC,
} from './registration-form.styled';
import type { RegistrationFormProps } from './types';
import useFormActions from './use-form-actions';

export const RegistrationForm: FC<RegistrationFormProps> = ({
  hideHeadline,
  headlineMessage,
  subheadlineMessage,
  onSubmitSuccess,
  externalSubmit,
  onError,
  emailOnly,
  neededFields = ['firstName', 'lastName', 'email', 'password', 'tandcCheck'],
  handleBlur,
  handleChange,
  submitAfterRecaptcha = true,
  ctaMessage,
  signupChannel,
  cvUploadConfig,
  enableGoogleSignup,
  enableAppleSignup,
  prefilledValues = {},
}) => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const extraParams = getExtraParams(urlParams);
  const loginPath =
    useLoginAppUrl() + `?signup_channel=${signupChannel}${extraParams}`;
  const host = useHost();
  const mainHostUrl = getMainHost(host.getHostname());
  const isXingbox =
    mainHostUrl.includes('kenv.xing.com') ||
    mainHostUrl.includes('oenv.xing.com');
  const FRIENDLYCAPTCHA_SITE_KEY = isXingbox
    ? FRIENDLYCAPTCHA_KEY_XB
    : FRIENDLYCAPTCHA_KEY;
  const { trackPageView } = useLoginTracking();
  const { formatMessage, locale } = useIntl();
  const {
    formErrors,
    values,
    setValues,
    handleResetCvUpload,
    maybeError,
    translateError,
    handleInputChange,
    handleEmailChange,
    showRecaptcha,
    onSubmit,
    isSubmitting,
  } = useFormActions({
    signupChannel,
    handleChange,
    prefilledValues,
    neededFields,
    emailOnly,
    externalUrl: mainHostUrl,
    submitAfterRecaptcha,
    onSubmitSuccess,
    externalSubmit,
    onError,
  });

  // Temporarily hide mandatory CV upload in RegistrationForm while PROGRAM-3403
  // changes CV enrichment. Set this to false or remove this gate for OG-2245.
  const isCvUploadTemporarilyHidden: boolean = true;
  const visibleCvUploadConfig = isCvUploadTemporarilyHidden
    ? undefined
    : cvUploadConfig;

  return showRecaptcha ? (
    <FriendlyCaptcha
      siteKey={FRIENDLYCAPTCHA_SITE_KEY}
      locale={locale}
      onVerify={(token) =>
        setValues((currentValues) => ({
          ...currentValues,
          captchaResponse: token,
        }))
      }
    />
  ) : (
    <FormContainer
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      data-testid="registration-form"
    >
      {!hideHeadline ? (
        <FormHeadline
          headlineMessage={formatMessage(
            headlineMessage || messages.registrationHeadline
          )}
          subheadlineMessage={
            subheadlineMessage ? (
              formatMessage(subheadlineMessage)
            ) : (
              <FormattedMessage
                {...messages.registrationSubheadline}
                values={{
                  signin: (
                    <a href={loginPath} target="_blank" rel="noreferrer">
                      <FormattedMessage {...messages.loginHeadline} />
                    </a>
                  ),
                }}
              />
            )
          }
        />
      ) : null}

      {enableGoogleSignup && !visibleCvUploadConfig ? (
        <ContinueWithGoogle
          signupChannel={
            signupChannel ? `${signupChannel}_oauth_google` : undefined
          }
          onClick={() => {
            trackPageView({
              page: 'signup/google',
              PropTrackAction: 'lp_google_click',
            });
          }}
        />
      ) : null}
      {enableAppleSignup && !visibleCvUploadConfig ? (
        <ContinueWithApple
          signupChannel={
            signupChannel ? `${signupChannel}_oauth_apple` : undefined
          }
          onClick={() => {
            trackPageView({
              page: 'signup/apple',
              PropTrackAction: 'lp_apple_click',
            });
          }}
        />
      ) : null}

      {visibleCvUploadConfig &&
      visibleCvUploadConfig.source &&
      visibleCvUploadConfig.flowIdentifier ? (
        <RegisterViaCvSection
          handleResetCvUpload={handleResetCvUpload}
          cvUploadConfig={visibleCvUploadConfig}
        />
      ) : enableAppleSignup || enableGoogleSignup ? (
        <Divider>
          <FormattedMessage {...messages.separator} />
        </Divider>
      ) : null}

      <div>
        <FakeInput name="email" aria-hidden tabIndex={-1} />
        <FakeInput type="password" name="password" aria-hidden tabIndex={-1} />
        {!emailOnly && neededFields.includes('firstName') && (
          <InputWrapper>
            <FormField
              autoComplete={'given-name'}
              size="medium"
              error={Boolean(maybeError('firstName'))}
              helperText={maybeError('firstName')}
              name="firstName"
              id="firstName"
              onBlur={(e) => handleBlur?.(e)}
              onChange={handleInputChange}
              label={formatMessage(messages.firstNameLabel)}
              value={values?.firstName}
              data-testid="firstName"
              required
            />
          </InputWrapper>
        )}
        {!emailOnly && neededFields.includes('lastName') && (
          <InputWrapper>
            <FormField
              autoComplete={'family-name'}
              size="medium"
              error={Boolean(maybeError('lastName'))}
              helperText={maybeError('lastName')}
              label={formatMessage(messages.lastNameLabel)}
              name="lastName"
              id="lastName"
              onBlur={(e) => handleBlur?.(e)}
              onChange={handleInputChange}
              value={values?.lastName}
              data-testid="lastName"
              required
            />
          </InputWrapper>
        )}
        {visibleCvUploadConfig &&
          visibleCvUploadConfig.source &&
          visibleCvUploadConfig.flowIdentifier &&
          values?.parsedCvId && (
            <HiddenWrapper>
              <FormField
                size="medium"
                name="parsedCvId"
                id="parsedCvId"
                onChange={handleInputChange}
                value={values.parsedCvId}
                data-testid="parsedCvId"
              />
            </HiddenWrapper>
          )}
        {neededFields.includes('email') && (
          <InputWrapper>
            <FormField
              autoComplete={'email'}
              size="medium"
              error={Boolean(
                maybeError('email') ||
                (formErrors.emailDomainError ??
                  translateError(formErrors.emailDomain))
              )}
              helperText={
                maybeError('email') ||
                (formErrors.emailDomainError &&
                  translateError(formErrors.emailDomain)) ||
                undefined
              }
              name="email"
              id="email"
              type="email"
              onBlur={(e) => handleBlur?.(e)}
              onChange={handleEmailChange}
              label={formatMessage(messages.emailLabel)}
              value={values?.email}
              data-testid="email"
              required
            />
          </InputWrapper>
        )}
        {neededFields.includes('password') && (
          <InputWrapper>
            <PasswordInput
              autoComplete={'new-password'}
              size="medium"
              error={Boolean(maybeError('password'))}
              name="password"
              id="password"
              onBlur={(e) => handleBlur?.(e)}
              onChange={handleInputChange}
              label={formatMessage(messages.passwordLabel)}
              value={values?.password}
              data-testid="password"
              required
            />
            <PasswordStrengthMeter
              password={values?.password || ''}
              error={maybeError('password')}
            />
          </InputWrapper>
        )}
        {neededFields.includes('captchaResponse') && (
          <FriendlyCaptcha
            siteKey={FRIENDLYCAPTCHA_SITE_KEY}
            locale={locale}
            onVerify={(token) =>
              setValues((currentValues) => ({
                ...currentValues,
                captchaResponse: token,
              }))
            }
          />
        )}

        <>
          <FormMessage error={translateError(formErrors?.base)} />
        </>
        {formErrors.emailDomainError && (
          <>
            <FormMessage notice={formatMessage(messages.emailDomainError)} />
            <InputWrapper>
              <Checkbox
                on={values?.skipEmailDomainCheck}
                name="skipEmailDomainCheck"
                id="skipEmailDomainCheck"
                onChange={(event) => {
                  setValues((values) => ({
                    ...values,
                    skipEmailDomainCheck: event.target.checked,
                  }));
                }}
              />
              <Label htmlFor="skipEmailDomainCheck">
                <FormattedMessage
                  {...messages.skipEmailDomainCheck}
                  values={{ email: <b>{values?.email}</b> }}
                />
              </Label>
            </InputWrapper>
          </>
        )}
        {formErrors.secondaryEmailError ? (
          <InputWrapper>
            <FormMessage notice={formatMessage(messages.secondaryEmailError)} />
            <CheckboxWrapper>
              <Checkbox
                on={!values?.checkSecondaryFields}
                name="checkSecondaryFields"
                id="checkSecondaryFields"
                onChange={(event) => {
                  setValues((values) => ({
                    ...values,
                    checkSecondaryFields: !event.target.checked,
                  }));
                }}
              />
              <label htmlFor="checkSecondaryFields">
                <FormattedMessage
                  {...messages.skipSecondaryEmailCheck}
                  values={{ email: <b>{values?.email}</b> }}
                />
              </label>
            </CheckboxWrapper>
          </InputWrapper>
        ) : null}
        <SubmitButton
          variant="primary"
          size="medium"
          loading={isSubmitting}
          type="submit"
          data-testid="register-submit-button"
          aria-label={formatMessage(messages.buttonContinueAriaLabel)}
        >
          {formatMessage(ctaMessage || messages.submitLabel)}
        </SubmitButton>

        {neededFields.includes('tandcCheck') ? (
          <InputActions>
            <TandC size="xsmall" sizeConfined="xsmall">
              <FormattedMessage
                {...messages.tandcDescription}
                values={{
                  tandc: (
                    <a
                      href="https://www.xing.com/terms"
                      target="'_blank"
                      rel="noreferrer"
                    >
                      <FormattedMessage {...messages.tandcLabel} />
                    </a>
                  ),
                  dataprotection: (
                    <a
                      href={formatMessage(messages.dataprotectionLink)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FormattedMessage {...messages.dataprotectionLabel} />
                    </a>
                  ),
                }}
              />
            </TandC>
          </InputActions>
        ) : null}
      </div>
    </FormContainer>
  );
};
